import { CancelTokenSource } from 'axios';

import {
  Address,
  AddressValidationResult,
  EnrichedAddress,
  Location,
  ShippingPointType,
} from '@rygen/rygen-ui/model';

import {
  EmbeddableContact,
  LocationSearchCriteria,
  LocationSearchResult,
  LocationQuickSearchResult,
} from '@/model';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'shipping-point';

type QuickSearchOptions = {
  shippingPointTypes?: ShippingPointType[];
  includeAddressBook?: boolean;
  includeTrimble?: boolean;
  cancelToken?: CancelTokenSource;
};

export class ShippingPointController extends BaseHttpController {
  constructor() {
    super(endpoint, { canIncludeSubDomains: true });
  }

  async save(location: Location): Promise<Location> {
    const response = await this.http.post<Location>('', location);
    return new Location(response.data);
  }

  async update(location: Location): Promise<Location> {
    const response = await this.http.put<Location>(`${location.id}`, location);
    return new Location(response.data);
  }

  async findPrimaryContactForShippingPoint(
    shippingPointId: number,
  ): Promise<EmbeddableContact> {
    const response = await this.http.get<EmbeddableContact>(
      `${shippingPointId}/contact`,
    );
    return new EmbeddableContact(response.data);
  }

  async search(
    payload: LocationSearchCriteria,
    cancelToken?: CancelTokenSource,
  ): Promise<LocationSearchResult> {
    const response = await this.http.post<LocationSearchResult>(
      'search',
      payload,
      { cancelToken: cancelToken?.token },
    );
    return new LocationSearchResult(response.data);
  }

  async getAutocompleteSuggestions(
    address: Address,
    cancelToken?: CancelTokenSource,
  ): Promise<EnrichedAddress[]> {
    const response = await this.http.post<EnrichedAddress[]>(
      'autocomplete',
      address,
      { cancelToken: cancelToken?.token },
    );
    return response.data.map((val) => new EnrichedAddress(val));
  }

  async getAutocompleteZipSuggestions(
    address: Address,
    cancelToken?: CancelTokenSource,
  ): Promise<EnrichedAddress[]> {
    const response = await this.http.post<EnrichedAddress[]>(
      'autocomplete/zip',
      address,
      {
        cancelToken: cancelToken?.token,
      },
    );
    return response.data.map((val) => new EnrichedAddress(val));
  }

  async validateAddress(
    address: Address,
    cancelToken?: CancelTokenSource,
  ): Promise<AddressValidationResult> {
    const response = await this.http.post<AddressValidationResult>(
      'validate',
      address,
      { cancelToken: cancelToken?.token },
    );
    return new AddressValidationResult(response.data);
  }

  async getGeocodeData(
    address: Address,
    cancelToken?: CancelTokenSource,
  ): Promise<EnrichedAddress> {
    const response = await this.http.post<EnrichedAddress>('geocode', address, {
      cancelToken: cancelToken?.token,
    });
    return new EnrichedAddress(response.data);
  }

  async quickSearch(
    searchText: string,
    {
      shippingPointTypes = [],
      includeAddressBook = true,
      includeTrimble = true,
      cancelToken,
    }: QuickSearchOptions,
  ): Promise<Location[]> {
    const response = await this.http.post<LocationQuickSearchResult>(
      'quick-search',
      { searchText, shippingPointTypes, includeAddressBook, includeTrimble },
      { cancelToken: cancelToken?.token },
    );

    return new LocationQuickSearchResult(response.data).results;
  }
}
